/**
 * Cookie Provider
 * 
 * Wraps the app and manages cookie consent globally
 * Conditionally loads analytics and tracking scripts based on consent
 */

import { createContext, useContext, useEffect, ReactNode, lazy, Suspense } from 'react';
import { useCookieConsent, CookieConsent } from '@/hooks/useCookieConsent';
// Lazy load CookieBanner to prevent blocking if content blocker interferes
const CookieBanner = lazy(() => 
  import('./CookieBanner')
    .then(module => ({ default: module.CookieBanner }))
    .catch(() => {
      // Return empty component if blocked
      console.warn('CookieBanner blocked by content blocker - continuing without it');
      return { default: () => null };
    })
);

interface CookieContextValue {
  consent: CookieConsent | null;
  hasConsent: (category: 'necessary' | 'functional' | 'analytics' | 'marketing') => boolean;
  hasGivenConsent: () => boolean;
  reopenBanner: () => void;
}

const CookieContext = createContext<CookieContextValue | undefined>(undefined);

export function useCookieContext() {
  const context = useContext(CookieContext);
  if (!context) {
    throw new Error('useCookieContext must be used within CookieProvider');
  }
  return context;
}

interface CookieProviderProps {
  children: ReactNode;
}

/**
 * Cookie Provider Component
 * Manages global cookie consent state and conditionally loads external scripts
 */
export function CookieProvider({ children }: CookieProviderProps) {
  const cookieConsent = useCookieConsent();
  const { consent, hasConsent } = cookieConsent;

  // Handle analytics initialization based on consent
  useEffect(() => {
    if (!consent) return;

    // Analytics consent granted
    if (hasConsent('analytics')) {
      initializeAnalytics();
    } else {
      disableAnalytics();
    }

    // Marketing consent granted
    if (hasConsent('marketing')) {
      initializeMarketing();
    } else {
      disableMarketing();
    }
  }, [consent, hasConsent]);

  // Listen for consent changes
  useEffect(() => {
    const handleConsentChange = (event: Event) => {
      const customEvent = event as CustomEvent<CookieConsent | null>;
      const newConsent = customEvent.detail;

      if (!newConsent) {
        // Consent withdrawn - clean up
        disableAnalytics();
        disableMarketing();
      }
    };

    window.addEventListener('cookieConsentChange', handleConsentChange);
    return () => window.removeEventListener('cookieConsentChange', handleConsentChange);
  }, []);

  const contextValue: CookieContextValue = {
    consent: cookieConsent.consent,
    hasConsent: cookieConsent.hasConsent,
    hasGivenConsent: cookieConsent.hasGivenConsent,
    reopenBanner: cookieConsent.reopenBanner,
  };

  return (
    <CookieContext.Provider value={contextValue}>
      {children}
      <Suspense fallback={null}>
        <CookieBanner />
      </Suspense>
    </CookieContext.Provider>
  );
}

/**
 * Initialize analytics scripts
 * Internal privacy-preserving analytics only
 */
function initializeAnalytics() {
  // Analytics initialized
  
  // Internal analytics is initialized in client/src/lib/analytics.ts
  // Tracks only with consent, respects DNT, fully anonymous
  
  // Example: Google Analytics
  // if (typeof window.gtag === 'function') {
  //   window.gtag('consent', 'update', {
  //     analytics_storage: 'granted'
  //   });
  // }

  // Set a flag that analytics components can check
  if (typeof window !== 'undefined') {
    (window as any).__analyticsEnabled = true;
  }
}

/**
 * Disable analytics
 */
function disableAnalytics() {
  // Analytics disabled
  
  // Example: Google Analytics
  // if (typeof window.gtag === 'function') {
  //   window.gtag('consent', 'update', {
  //     analytics_storage: 'denied'
  //   });
  // }

  if (typeof window !== 'undefined') {
    (window as any).__analyticsEnabled = false;
  }
}

/**
 * Initialize marketing scripts
 */
function initializeMarketing() {
  // Marketing initialized
  
  // Example: Facebook Pixel
  // if (typeof window.fbq === 'function') {
  //   window.fbq('consent', 'grant');
  // }

  // Example: LinkedIn Insight Tag
  // if (typeof window._linkedin_data_partner_ids !== 'undefined') {
  //   // Re-initialize LinkedIn tag
  // }

  if (typeof window !== 'undefined') {
    (window as any).__marketingEnabled = true;
  }
}

/**
 * Disable marketing scripts
 */
function disableMarketing() {
  // Marketing disabled
  
  // Example: Facebook Pixel
  // if (typeof window.fbq === 'function') {
  //   window.fbq('consent', 'revoke');
  // }

  if (typeof window !== 'undefined') {
    (window as any).__marketingEnabled = false;
  }
}

/**
 * Utility component to conditionally render children based on consent
 */
interface ConditionalRenderProps {
  category: 'necessary' | 'functional' | 'analytics' | 'marketing';
  children: ReactNode;
  fallback?: ReactNode;
}

export function ConditionalRender({ 
  category, 
  children, 
  fallback = null 
}: ConditionalRenderProps) {
  const { hasConsent } = useCookieContext();
  
  return hasConsent(category) ? <>{children}</> : <>{fallback}</>;
}

